// Helpers for diary photos backed by Supabase Storage (`diary-photos` bucket).
// Photos are stored at: <user_id>/<entry_id>/<photo_id>.<ext>
// Records in the diary entry's `photos` array look like:
//   { id, path, url, name }
// The `url` is a signed URL refreshed at load time; only `path` is durable.

import { supabase } from './supabase';

const BUCKET = 'diary-photos';
const SIGNED_URL_TTL_SECONDS = 60 * 60 * 24; // 24h; refreshed each session

const extOf = (filename) => {
  const m = /\.([a-zA-Z0-9]+)$/.exec(filename || '');
  return (m && m[1]) || 'jpg';
};

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export async function uploadPhotos(userId, entryId, files) {
  const out = [];
  for (const f of files) {
    const id = newId();
    const path = `${userId}/${entryId}/${id}.${extOf(f.name)}`;
    const { error } = await supabase.storage.from(BUCKET).upload(path, f, {
      contentType: f.type || 'image/jpeg',
      upsert: false
    });
    if (error) throw error;
    const { data: signed, error: signErr } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
    if (signErr) throw signErr;
    out.push({ id, path, url: signed.signedUrl, name: f.name });
  }
  return out;
}

export async function deletePhoto(photo) {
  if (!photo || !photo.path) return;
  const { error } = await supabase.storage.from(BUCKET).remove([photo.path]);
  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[diaryPhotos] delete failed', error);
  }
}

// Refresh signed URLs for all photos that have a `path` (newer photos).
// Photos that only have `dataUrl` (legacy) are returned unchanged.
export async function refreshSignedUrls(photos) {
  if (!Array.isArray(photos) || photos.length === 0) return photos;
  const needs = photos.filter((p) => p && p.path);
  if (needs.length === 0) return photos;
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrls(
      needs.map((p) => p.path),
      SIGNED_URL_TTL_SECONDS
    );
  if (error) return photos;
  const byPath = new Map();
  (data || []).forEach((d, i) => byPath.set(needs[i].path, d.signedUrl));
  return photos.map((p) => (p && p.path ? { ...p, url: byPath.get(p.path) || p.url } : p));
}
